import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Grid,
	Typography,
	TextField,
	Autocomplete,
	Divider
} from '@mui/material';
import 'components/AlertKpi/alert-kpi.scss';
import PageHeader from 'components/Header/Page';
import KPIIcon from '../../assets/kpi-white.svg';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { checkRole } from 'utils/roleUtils';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomDropdown from 'components/Dropdown';
import { getKPIFilterOptions, getKpiFrameworkConfigList } from 'service/service';
import Loader from 'components/Loader/loader';
import { KPIConfigList } from './kpiConfigList';
import DialogueModal from 'components/Modal/DialogModal';
import { KPIForm } from './kpiForm';
import SuccessConfirmationDialog from './SuccessModal';
import moment from 'moment';
import PageWrapper from 'components/Common/PageWraper';

const lightDropdownSxProps = {
	backgroundColor: '#EFF6FF !important',
	borderRadius: '4px',
	height: '38px',
	border: 'none !important',
	fontWeight: '500 !important',
	'&.MuiInputBase-root': {
		backgroundColor: '#EFF6FF !important',
		height: '38px',
		border: 'none !important',
		fontWeight: '500 !important',
		'& *': {
			fontWeight: '500 !important'
		}
	},
	'& fieldset': {
		border: 'none !important'
	},
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'&:hover .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'& .MuiSelect-select': {
		fontSize: '14px !important',
		fontWeight: '500 !important',
		color: '#000000 !important',
		backgroundColor: '#EFF6FF !important',
		paddingTop: '8px !important',
		paddingBottom: '8px !important',
		height: '38px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		'& *': {
			fontWeight: '500 !important'
		}
	},
	'& .MuiSelect-icon': {
		color: '#39ACE7 !important',
		fill: '#39ACE7 !important'
	},
	'& em': {
		fontStyle: 'normal',
		color: '#64748b',
		fontWeight: '500 !important'
	}
};

const lightAutocompleteSxProps = {
	backgroundColor: '#EFF6FF !important',
	borderRadius: '4px',
	height: '38px',
	border: 'none !important',
	fontWeight: '500 !important',
	'& .MuiInputBase-root': {
		backgroundColor: '#EFF6FF !important',
		color: '#000000 !important',
		fontSize: '14px !important',
		fontWeight: '500 !important',
		height: '38px',
		paddingTop: '0 !important',
		paddingBottom: '0 !important',
		paddingLeft: '12px !important',
		paddingRight: '36px !important',
		display: 'flex',
		alignItems: 'center',
		border: 'none !important',
		'& *': {
			fontWeight: '500 !important'
		}
	},
	'& .MuiInputBase-input': {
		color: '#000000 !important',
		backgroundColor: 'transparent !important',
		fontSize: '14px !important',
		fontWeight: '500 !important',
		paddingTop: '8px !important',
		paddingBottom: '8px !important',
		height: 'auto !important',
		boxSizing: 'border-box',
		'&::placeholder': {
			color: '#64748b !important',
			fontWeight: '500 !important',
			opacity: 1
		}
	},
	'& fieldset': {
		border: 'none !important'
	},
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'&:hover .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'& .MuiAutocomplete-popupIndicator': {
		color: '#39ACE7 !important',
		'& svg': { fill: '#39ACE7 !important' }
	},
	'& .MuiAutocomplete-clearIndicator': {
		color: '#39ACE7 !important',
		'& svg': { fill: '#39ACE7 !important' }
	}
};

const lightMenuProps = {
	PaperProps: {
		sx: {
			backgroundColor: '#EFF6FF !important',
			color: '#000000 !important',
			border: '1px solid #cbd5e1',
			borderRadius: '4px',
			boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
			'& .MuiMenuItem-root': {
				color: '#000000 !important',
				backgroundColor: '#EFF6FF !important',
				fontSize: '14px !important',
				fontWeight: '500 !important',
				'& *': {
					fontWeight: '500 !important'
				},
				'&:hover': {
					backgroundColor: '#dbeafe !important',
					color: '#000000 !important'
				},
				'&.Mui-disabled': {
					color: '#94a3b8 !important',
					backgroundColor: '#EFF6FF !important'
				},
				'&.Mui-selected': {
					backgroundColor: '#bfdbfe !important',
					color: '#000000 !important',
					fontWeight: '500 !important'
				},
				'&.Mui-selected:hover': {
					backgroundColor: '#bfdbfe !important',
					color: '#000000 !important',
					fontWeight: '500 !important'
				}
			}
		}
	}
};

const lightAutocompleteComponentsProps = {
	paper: {
		sx: {
			backgroundColor: '#EFF6FF !important',
			color: '#000000 !important',
			border: '1px solid #cbd5e1',
			borderRadius: '4px',
			'& .MuiAutocomplete-option': {
				backgroundColor: '#EFF6FF !important',
				color: '#000000 !important',
				fontSize: '14px !important',
				fontWeight: '500 !important',
				'& *': {
					fontWeight: '500 !important'
				},
				'&:hover, &.Mui-focused': {
					backgroundColor: '#dbeafe !important',
					color: '#000000 !important'
				},
				'&.Mui-selected': {
					backgroundColor: '#bfdbfe !important',
					color: '#000000 !important',
					fontWeight: '500 !important'
				},
				'&.Mui-selected:hover': {
					backgroundColor: '#bfdbfe !important',
					color: '#000000 !important',
					fontWeight: '500 !important'
				}
			},
			'& .MuiAutocomplete-noOptions, & .MuiAutocomplete-noOptions *': {
				color: '#000000 !important',
				fontSize: '14px !important',
				fontWeight: '500 !important'
			}
		}
	},
	popper: {
		sx: {
			'& .MuiAutocomplete-noOptions, & .MuiAutocomplete-noOptions *': {
				color: '#000000 !important',
				fontSize: '14px !important',
				fontWeight: '500 !important'
			}
		}
	}
};

const lightTextFieldSx = {
	width: '100%',
	backgroundColor: '#EFF6FF !important',
	borderRadius: '4px',
	border: 'none !important',
	fontWeight: '500 !important',
	'& .MuiInputBase-root': {
		backgroundColor: '#EFF6FF !important',
		color: '#000000 !important',
		fontSize: '14px !important',
		fontWeight: '500 !important',
		height: '38px',
		border: 'none !important',
		'& *': {
			fontWeight: '500 !important'
		}
	},
	'& fieldset': {
		border: 'none !important'
	},
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'&:hover .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: 'none !important'
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 14px',
		color: '#000000 !important',
		fontSize: '14px !important',
		fontWeight: '500 !important',
		'&::placeholder': {
			color: '#64748b !important',
			fontWeight: '500 !important',
			opacity: 1
		}
	},
	'&.Mui-disabled, & .MuiInputBase-root.Mui-disabled': {
		backgroundColor: '#EFF6FF !important',
		opacity: 0.6,
		cursor: 'not-allowed',
		border: 'none !important',
		fontWeight: '500 !important'
	}
};

const KPIFramework = () => {
	const [filterData, setFilterData] = useState<any>({})
	const [loading, setLoading] = useState(false)
	const [kpiConfigs, setKpiConfigs] = useState([])
	const [count, setCount] = useState(0)
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10)
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [openDialogSuccess, setOpenDialogSuccess] = useState(false)
	const [controller] = usePepwiseAuthController();
	const { role } = controller;
	const allFilters = ['Program', 'Project', 'Sector', 'KPI Name', 'Status']
	const filterOptions: any = {
		'Program': { columnName: 'program', data: filterData.programs || [] },
		'Project': { columnName: 'project', data: filterData.projects || [] },
		'Sector': { columnName: 'sector', data: filterData.sectors || [] },
		'KPI Name': { columnName: 'name' },
		'Status': { columnName: 'isActive', data: ['Active', 'Inactive'] },
	}
	const [filters, setFilters] = useState([
		{ uuid: crypto.randomUUID(), field: 'Program', value: '' }
	]);

	const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);

	const handleAddFilter = (index?: number) => {
		const newFilter = { uuid: crypto.randomUUID(), field: '', value: '' };
		if (typeof index === 'number' && index >= 0) {
			const newFilters = [...filters];
			newFilters.splice(index + 1, 0, newFilter);
			setFilters(newFilters);
		} else {
			setFilters([...filters, newFilter]);
		}
	};

	const handleRemoveFilter = (uuid: string) => {
		const updated = filters.filter((filter: any) => filter.uuid !== uuid);
		if (updated.length === 0) {
			setFilters([{ uuid: crypto.randomUUID(), field: '', value: '' }]);
		} else {
			setFilters(updated);
		}
	};

	const handleFilterChange = (uuid: string, name: string, value: string | null) => {
		setFilters(filters.map((filter: any) => {
			if (filter.uuid === uuid) {
				const updated = { ...filter, [name]: value };
				if (name === 'field') {
					updated.value = '';
				}
				return updated;
			}
			return filter;
		}));
	};

	const getFieldDropdownOptions = (currentField: string) => {
		return allFilters.map((opt) => ({
			label: opt,
			value: opt,
			isDisabled: filters.some((f: any) => f.field === opt && f.field !== currentField)
		}));
	};

	const handleSorting = (newSortingInfo: any) => {
		setSortingInfo(newSortingInfo);
		fetchKPIConfigs({ ...defaultBody, sortColumn: newSortingInfo.sortBy, sortDir: newSortingInfo.sortDir });
	};

	const defaultBody = { page: 1, pageSize: 10, sortColumn: sortingInfo.sortBy, sortDir: sortingInfo.sortDir }

	const fetchKPIConfigs = async (body: any = defaultBody, isClear = false) => {
		const { page, pageSize, sortColumn, sortDir } = body?.page ? body : defaultBody;
		const filterObj = isClear ? {} : filters.filter(fl => !!fl.value && !!fl.field).reduce((filObj: any, fil) => {
			const filterConfig = filterOptions[fil.field]
			if (!filterConfig) return filObj
			const columnName = filterConfig.columnName
			if (columnName === 'isActive') {
				filObj[columnName] = fil.value === 'Active' ? true : false
			} else {
				filObj[columnName] = fil.value
			}
			return filObj
		}, {})
		setLoading(true)
		const response = await getKpiFrameworkConfigList({ filter: filterObj, page, pageSize, sortColumn: sortColumn === 'createdDt' ? 'createdAt' : sortColumn, sortDir })
		setLoading(false)
		if (response?.data) {
			response.data.data = response.data.data.map((item: any) => {
				item.update_frequency = `${item.update_frequency_value} ${item.update_frequency_unit}`
				item.source_type = item.source_config ? item.source_config.type : ''
				item.createdDt = item.createdAt ? moment(item.createdAt).format('yyyy-MM-DD HH:mm') : ''
				item.createdByUser = item.createdByUser?.[0] || ''
				item.updatedByUser = item.updatedByUser?.[0] || ''
				return item
			})
			setKpiConfigs(response.data.data)
			setCount(response.data.totalCount)
		}
	}

	const fetchOptions = async () => {
		const response = await getKPIFilterOptions()
		if (response.data) {
			setFilterData(response.data)
		}
	}

	const clearFilters = () => {
		setPage(1);
		setFilters([{ uuid: crypto.randomUUID(), field: 'Program', value: '' }]);
		fetchKPIConfigs(defaultBody, true);
	}

	useEffect(() => {
		fetchKPIConfigs(defaultBody)
		fetchOptions()
	}, [])

	return (
		<PageWrapper sx={{ marginTop: { xs: '70px', sm: '50px', md: '45px' }}}>

			<PageHeader title="KPI Store" icon={KPIIcon} headerSx={{ top: '78px', paddingTop: '16px' }}>
				{isAllowed && (
					<Button variant="pepwiseSecondary" onClick={() => setShowCreateModal(true)} sx={{ height: '44px', padding: '8px 24px' }}>
						+ Add KPI Config
					</Button>
				)}
			</PageHeader>
			<DialogueModal width='xl' title='Add KPI List' isOpen={showCreateModal}
				titleSx = {{padding: '16px 20px'}}
				handleClose={(ev, reason?: string) => {
					if (reason !== 'backdropClick') {
						setShowCreateModal(false)
					}
				}}>
				<KPIForm onCancel={() => setShowCreateModal(false)}
					onCreateKPI={() => { setShowCreateModal(false); setOpenDialogSuccess(true); fetchKPIConfigs(defaultBody) }} />
			</DialogueModal>
			<SuccessConfirmationDialog open={openDialogSuccess} onClose={() => { setOpenDialogSuccess(false) }} />

			<Box sx={{ backgroundColor: '#FBFDFF', borderRadius: '4px', margin: 2, p: 2, boxShadow: '4px 20px 44px 0px #00000026' }}>
				<Typography sx={{ fontSize: '16px', fontWeight: 600, color: 'text.primary', mb: 1 }}>
					Filters
				</Typography>

				<Grid container>
					<Grid item container xs={12}>
						<Divider style={{ borderColor: '#e2e8f0', width: '100%', margin: '12px 0' }} />
					</Grid>

					<Grid item container xs={12} spacing={2} alignItems="center" mb={1.5}>
						<Grid item xs={1.2} sx={{ minWidth: '76px' }} display="flex" alignItems="center">
							<Button
								variant="text"
								onClick={clearFilters}
								disableRipple
								sx={{
									fontSize: '12px',
									fontWeight: 700,
									color: '#A9ADCB !important',
									cursor: 'pointer',
									background: 'none !important',
									border: 'none',
									p: 0,
									minWidth: 'unset',
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									textTransform: 'uppercase',
									whiteSpace: 'nowrap',
									position: 'relative',
									zIndex: 10,
									'&:hover': {
										background: 'none !important',
										color: '#64748b !important'
									}
								}}
							>
								<span style={{ fontSize: '16px', lineHeight: 1, color: '#A9ADCB' }}>✕</span> CLEAR ALL
							</Button>
						</Grid>
						<Grid item xs={5} sx={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
							Field
						</Grid>
						<Grid item xs={5.8} sx={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
							Value
						</Grid>
					</Grid>

					<Grid item container xs={12}>
						<Grid item container xs={12} spacing={2} display="flex" alignItems="center">
							{filters.map((filter, idx) => (
								<React.Fragment key={filter.uuid}>
									<Grid item xs={1.2} sx={{ minWidth: '76px' }} display="flex" gap={0.8} alignItems="center">
										<Button
											onClick={() => handleRemoveFilter(filter.uuid)}
											sx={{
												minWidth: '32px',
												width: '32px',
												height: '32px',
												p: 0,
												border: '1px solid #fca5a5',
												borderRadius: '4px',
												color: '#ef4444',
												backgroundColor: '#fff',
												'&:hover': { backgroundColor: '#fef2f2', borderColor: '#ef4444' }
											}}
										>
											<CloseIcon sx={{ fontSize: '20px' }} />
										</Button>

										<Button
											onClick={() => handleAddFilter(idx)}
											sx={{
												minWidth: '32px',
												width: '32px',
												height: '32px',
												p: 0,
												border: '1px solid #38bdf8',
												borderRadius: '4px',
												color: '#009BD8',
												backgroundColor: '#fff',
												'&:hover': { backgroundColor: '#f0f9ff', borderColor: '#009BD8' }
											}}
										>
											<AddIcon sx={{ fontSize: '20px' }} />
										</Button>
									</Grid>

									<Grid item xs={5}>
										<CustomDropdown
											bgColor="#EFF6FF"
											options={getFieldDropdownOptions(filter.field)}
											value={filter.field || ''}
											handleChange={(e) => handleFilterChange(filter.uuid, 'field', e.target.value)}
											selectComponentProps={{
												displayEmpty: true,
												sx: lightDropdownSxProps
											}}
											menuProps={lightMenuProps}
											dropdownIcon={ExpandMoreIcon}
											noSelectionLabel="Select Field"
											boxProps={{
												sx: { width: '100%', border: 'none !important', '& *': { fontWeight: '500 !important' } }
											}}
										/>
									</Grid>

									<Grid item xs={5.8}>
										{!filter.field ? (
											<TextField
												fullWidth
												disabled
												size="small"
												placeholder="Select value"
												variant="outlined"
												value=""
												sx={lightTextFieldSx}
											/>
										) : filterOptions[filter.field]?.data ? (
											<Autocomplete
												size="small"
												value={filter.value || null}
												onChange={(e, option) => handleFilterChange(filter.uuid, 'value', option)}
												options={filterOptions[filter.field]?.data || []}
												popupIcon={<ExpandMoreIcon sx={{ color: '#39ACE7 !important', fill: '#39ACE7 !important' }} />}
												componentsProps={lightAutocompleteComponentsProps}
												sx={lightAutocompleteSxProps}
												renderInput={(params) => (
													<TextField
														{...params}
														placeholder="Select value"
														variant="outlined"
														inputProps={{
															...params.inputProps,
															style: { ...params.inputProps?.style, fontWeight: 500 }
														}}
													/>
												)}
											/>
										) : (
											<TextField
												fullWidth
												size="small"
												placeholder="Enter value"
												variant="outlined"
												value={filter.value || ''}
												onChange={(e) => handleFilterChange(filter.uuid, 'value', e.target.value)}
												sx={lightTextFieldSx}
											/>
										)}
									</Grid>
								</React.Fragment>
							))}
						</Grid>
					</Grid>

					<Grid item container xs={12} justifyContent="flex-end" mt={2}>
						<Button
							variant="outlined"
							onClick={() => fetchKPIConfigs(defaultBody)}
							sx={{
								minWidth: '140px',
								height: '38px',
								color: '#39ACE7 !important',
								borderColor: '#39ACE7 !important',
								borderRadius: '4px',
								fontWeight: 600,
								textTransform: 'uppercase',
								'&:hover': {
									borderColor: '#0078b4',
									backgroundColor: 'rgba(57, 172, 231, 0.08)',
								},
								'&.Mui-disabled': {
									borderColor: 'rgba(57, 172, 231, 0.5) !important',
									color: '#39ACE7 !important',
									opacity: 0.7
								}
							}}
						>
							RUN QUERY
						</Button>
					</Grid>
				</Grid>
			</Box>
			{loading ? <Loader /> :
				<KPIConfigList kpiConfigs={kpiConfigs} totalCount={count} updateConfigs={fetchKPIConfigs}
					page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} sortingInfo={sortingInfo}
					setSortingInfo={setSortingInfo} handleSorting={handleSorting} />}
		</PageWrapper>
	);
};

export default KPIFramework;
